import { useEffect, useRef, type HTMLAttributes, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import {
  canUseAppFromAccess,
  hasAccessDecision,
  isOrgDisclaimerRequired,
  shouldShowAccessDeniedFromAccess,
  shouldShowPricingFromAccess,
} from "@/helpers";
import OrgDisclaimerGateScreen from "@/features/auth/components/access/OrgDisclaimerGateScreen";
import PageLoader from "./PageLoader";

interface ProtectedRouteProps {
  children: ReactNode;
  requiresAppAccess?: boolean;
  requiresActiveSubscription?: boolean;
}

function ProtectedRoute({
  children,
  requiresAppAccess = false,
  requiresActiveSubscription = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, subscriptionStatus, user, accessDecision } =
    useAuth();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const location = useLocation();
  const hasShownToastRef = useRef(false);
  const lastPathRef = useRef<string>("");

  // Reset toast flag when location changes
  useEffect(() => {
    if (location.pathname !== lastPathRef.current) {
      hasShownToastRef.current = false;
      lastPathRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <PageLoader />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Show toast only once when redirecting
    if (!hasShownToastRef.current) {
      hasShownToastRef.current = true;
      showToast({
        title: t("features.auth.session.notLoggedInTitle"),
        description: t("features.auth.session.notLoggedInDescription"),
        variant: "destructive",
      });
    }
    return <Navigate to="/login" replace />;
  }

  const isAccountCompletionRequired =
    user?.user.account_completion_required === true;
  const isAccountCompletionRoute = location.pathname === "/auth/account/complete";

  if (isAccountCompletionRequired && !isAccountCompletionRoute) {
    return <Navigate to="/auth/account/complete" replace />;
  }

  const shouldRequireAppAccess =
    requiresAppAccess || requiresActiveSubscription;

  if (
    shouldRequireAppAccess &&
    isOrgDisclaimerRequired(user?.org_disclaimer)
  ) {
    return (
      <>
        <div
          data-testid="org-disclaimer-inert-app"
          aria-hidden="true"
          className="pointer-events-none select-none"
          {...({ inert: "" } as HTMLAttributes<HTMLDivElement>)}
        >
          {children}
        </div>
        <OrgDisclaimerGateScreen />
      </>
    );
  }

  if (shouldRequireAppAccess && hasAccessDecision(accessDecision)) {
    const canUseApp = canUseAppFromAccess(accessDecision);
    const shouldShowPricing = shouldShowPricingFromAccess(accessDecision);
    const shouldShowAccessDenied =
      shouldShowAccessDeniedFromAccess(accessDecision);

    if (shouldShowPricing === true) {
      return <Navigate to="/pricing" replace />;
    }

    if (shouldShowAccessDenied === true || canUseApp === false) {
      return <Navigate to="/auth/access-denied" replace />;
    }

    if (canUseApp === true) {
      return <>{children}</>;
    }
  }

  // Only routes that require active subscription should block on subscription data
  if (shouldRequireAppAccess && !subscriptionStatus) {
    return <PageLoader />;
  }

  // Check subscription status if required for this route
  if (shouldRequireAppAccess && subscriptionStatus) {
    const isSubscriptionExpired =
      subscriptionStatus.subscription_status === "expired" ||
      subscriptionStatus.subscription_status === "cancelled" ||
      subscriptionStatus.subscription_status === "past_due" ||
      (subscriptionStatus.subscription_status === "freemium" &&
        subscriptionStatus.subscription_trial_remaining === 0);

    if (isSubscriptionExpired) {
      return <Navigate to="/auth/subscription-expired" replace />;
    }
  }

  // User is authenticated with valid subscription (if required), render the protected content
  return <>{children}</>;
}

export default ProtectedRoute;
